/**
 * ChangePolicyModal — set how change-requests to your record are handled.
 *
 * Phase G.7.6 (sacred/change-doctrine.md §VII Auto-Response Policy):
 *
 *   Default policy (applies to all incoming requests unless overridden):
 *     - manual       — default-of-defaults; you must respond
 *     - auto_accept  — accept all; the audit log preserves the trail
 *     - auto_ignore  — let the cosmos accept after 30 days; you stay quiet
 *
 *   Per-Orchist override (specific other Orchist, more flexible):
 *     - manual | auto_accept | auto_reject | auto_ignore
 *     - clear (revert to default)
 *
 *   Doctrine: auto_reject is NOT allowed as global default. Free-will is the
 *   practice; rejecting all proposals from everyone is hostile to recognition
 *   itself. But a specific cooled relationship may earn auto_reject — that's
 *   why it's available per-Orchist.
 *
 *   "No flooding" is the surface concern. With many invitees, an Orchist may
 *   want to default-accept proposals from anyone they've invited, and only
 *   manually review one specific person they're calibrating with. The policy
 *   surface lets them shape that.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  useChangePolicy,
  type DefaultPolicy,
  type OverridePolicy,
} from '@/hooks/useChangePolicy';
import { useLineage, lineageDisplayString } from '@/hooks/useLineage';
import { useNicknames } from '@/hooks/useNicknames';
import { resolveTimezone } from '@/lib/dates';
import {
  OrchistSearchInput,
  matchesOrchistQuery,
} from '@/components/OrchistSearch';

export interface ChangePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_LABELS: Record<DefaultPolicy, { title: string; hint: string }> = {
  manual: {
    title: 'Manual',
    hint: 'I will respond to each proposal myself. (default-of-defaults)',
  },
  auto_accept: {
    title: 'Auto-accept',
    hint: 'Apply every proposal to my record immediately. Audit log preserves the trail.',
  },
  auto_ignore: {
    title: 'Auto-ignore',
    hint: 'Stay quiet. The cosmos will accept after 30 days of silence.',
  },
};

const OVERRIDE_LABELS: Record<OverridePolicy, { title: string; hint: string }> = {
  manual: { title: 'Manual', hint: 'Respond myself' },
  auto_accept: { title: 'Auto-accept', hint: 'Apply immediately' },
  auto_reject: { title: 'Auto-reject', hint: 'Decline immediately (per-Orchist only)' },
  auto_ignore: { title: 'Auto-ignore', hint: 'Wait 30 days, cosmos accepts' },
};

export function ChangePolicyModal({ isOpen, onClose }: ChangePolicyModalProps) {
  const { contact } = useAuth();
  const { policy, loading, setDefault, setOverride } = useChangePolicy();
  const { rows: lineage } = useLineage();
  const { byTargetId: nicknamesByTargetId } = useNicknames();
  const tz = resolveTimezone(contact?.timezone);

  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [overrideSearch, setOverrideSearch] = useState('');

  // Related Orchists: those I've invited + the one who invited me (if any)
  const related = useMemo(() => {
    if (!contact) return [];
    const invitedByMe = lineage.filter((l) => l.invitationSentBy === contact.id);
    const myInviter = lineage.find(
      (l) => contact.invitationSentBy && l.id === contact.invitationSentBy
    );
    const all = myInviter ? [myInviter, ...invitedByMe] : invitedByMe;
    // Dedupe + skip self
    const seen = new Set<string>();
    return all.filter((r) => r.id !== contact.id && !seen.has(r.id) && (seen.add(r.id), true));
  }, [contact, lineage]);

  // FAB-filtered subset of related (Fuzzy + Boolean engine)
  const filteredRelated = useMemo(() => {
    if (!overrideSearch.trim()) return related;
    return related.filter((r) =>
      matchesOrchistQuery(r, nicknamesByTargetId.get(r.id) ?? null, overrideSearch, tz)
    );
  }, [related, overrideSearch, nicknamesByTargetId, tz]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setSavingState('idle');
      setErrorMessage(null);
      setOverridesOpen(false);
      setOverrideSearch('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentDefault = policy.default ?? 'manual';

  async function handleDefaultChange(p: DefaultPolicy) {
    if (savingState === 'saving') return;
    setSavingState('saving');
    setErrorMessage(null);
    try {
      await setDefault(p);
      setSavingState('idle');
    } catch (err) {
      setSavingState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Could not save default.');
    }
  }

  async function handleOverrideChange(targetId: string, p: OverridePolicy | 'clear') {
    if (savingState === 'saving') return;
    setSavingState('saving');
    setErrorMessage(null);
    try {
      await setOverride(targetId, p);
      setSavingState('idle');
    } catch (err) {
      setSavingState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Could not save override.');
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center px-6 py-12">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-paper/80 backdrop-blur-sm cursor-default"
      />

      <div className="relative w-full max-w-lg bg-paper border border-ink/20 shadow-xl p-8 space-y-6 max-h-[90vh] overflow-y-auto">
        <div className="text-center space-y-2">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            Change-request preferences
          </p>
          <h2 className="display text-3xl font-medium leading-tight">
            <span className="italic text-accent">How do you want to be asked?</span>
          </h2>
          <p className="text-sm text-ink/55 italic font-display max-w-sm mx-auto leading-relaxed">
            With many invitees, proposals may pile up. Set a default; override
            for specific Orchists. The audit log preserves every gesture.
          </p>
        </div>

        {/* Default policy — contemplative radio list, no boxy clutter */}
        <fieldset className="space-y-0.5">
          <legend className="text-xs tracking-wide uppercase text-ink/55 mb-2">
            Default
          </legend>
          {(['manual', 'auto_accept', 'auto_ignore'] as DefaultPolicy[]).map((p) => {
            const active = currentDefault === p;
            return (
              <label
                key={p}
                className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors border-l-2 ${
                  active
                    ? 'border-l-accent bg-accent/5'
                    : 'border-l-transparent hover:bg-paper/50'
                }`}
              >
                <input
                  type="radio"
                  name="default-policy"
                  value={p}
                  checked={active}
                  onChange={() => handleDefaultChange(p)}
                  disabled={savingState === 'saving'}
                  className="accent-accent mt-1 cursor-pointer flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p
                    className={`display text-sm leading-tight ${
                      active ? 'text-ink font-medium' : 'text-ink/75'
                    }`}
                  >
                    {DEFAULT_LABELS[p].title}
                  </p>
                  <p className="text-[0.7rem] text-ink/50 italic font-display leading-snug mt-0.5">
                    {DEFAULT_LABELS[p].hint}
                  </p>
                </div>
              </label>
            );
          })}
          <p className="text-[0.65rem] text-ink/40 italic font-display leading-relaxed pl-3 pt-3">
            Auto-reject as default is not allowed &mdash; free-will is the
            practice. To auto-reject a specific Orchist, use the per-Orchist
            overrides below.
          </p>
        </fieldset>

        {/* Per-Orchist overrides */}
        {related.length > 0 && (
          <section className="border-t border-ink/10 pt-6">
            <button
              type="button"
              onClick={() => setOverridesOpen((v) => !v)}
              className="w-full text-left flex items-center justify-between text-sm tracking-wide uppercase text-ink/60 hover:text-ink/80 transition-colors"
            >
              <span>Per-Orchist overrides ({related.length} related)</span>
              <span className="text-[0.65rem]">{overridesOpen ? '— hide' : '+ show'}</span>
            </button>

            {overridesOpen && (
              <div className="mt-4 space-y-4">
                {related.length > 5 && (
                  <OrchistSearchInput
                    value={overrideSearch}
                    onChange={setOverrideSearch}
                    placeholder="Search related Orchists..."
                    summary={
                      overrideSearch
                        ? `${filteredRelated.length} of ${related.length} match`
                        : undefined
                    }
                  />
                )}
                <ul className="space-y-2 list-none pl-0">
                {(filteredRelated.length === 0 && overrideSearch.trim()
                  ? []
                  : filteredRelated
                ).map((r) => {
                  const display = lineageDisplayString(r, tz);
                  const currentOverride = policy.perOrchist?.[r.id];
                  return (
                    <li
                      key={r.id}
                      className="border-l-2 border-l-ink/10 px-3 py-2 space-y-1.5"
                    >
                      <p className="display text-sm truncate">{display}</p>
                      <div className="flex flex-wrap gap-1.5 text-xs">
                        {(['manual', 'auto_accept', 'auto_reject', 'auto_ignore'] as OverridePolicy[]).map((p) => {
                          const active = currentOverride === p;
                          return (
                            <button
                              key={p}
                              type="button"
                              onClick={() => handleOverrideChange(r.id, p)}
                              disabled={savingState === 'saving'}
                              className={`px-2.5 py-1 border text-[0.7rem] italic font-display transition-colors disabled:opacity-50 ${
                                active
                                  ? 'border-accent bg-accent/10 text-ink'
                                  : 'border-ink/15 text-ink/55 hover:border-ink/40 hover:text-ink'
                              }`}
                              title={OVERRIDE_LABELS[p].hint}
                            >
                              {OVERRIDE_LABELS[p].title}
                            </button>
                          );
                        })}
                        {currentOverride && (
                          <button
                            type="button"
                            onClick={() => handleOverrideChange(r.id, 'clear')}
                            disabled={savingState === 'saving'}
                            className="px-2 py-1 text-[0.7rem] italic underline underline-offset-4 decoration-ink/20 text-ink/40 hover:text-ink/70 hover:decoration-ink/60 transition-colors disabled:opacity-50"
                          >
                            clear
                          </button>
                        )}
                      </div>
                      {!currentOverride && (
                        <p className="text-[0.65rem] text-ink/35 italic">
                          using default ({DEFAULT_LABELS[currentDefault].title})
                        </p>
                      )}
                    </li>
                  );
                })}
                </ul>
                {filteredRelated.length === 0 && overrideSearch.trim() && (
                  <p className="text-sm italic text-ink/40 font-display text-center py-4">
                    No related Orchists match your search.
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {loading && (
          <p className="text-xs italic text-ink/40 font-display text-center">Reading...</p>
        )}
        {savingState === 'saving' && (
          <p className="text-xs italic text-ink/55 font-display text-center">Saving...</p>
        )}
        {errorMessage && (
          <p className="text-sm text-destructive text-center" role="alert">
            {errorMessage}
          </p>
        )}

        <button
          type="button"
          onClick={onClose}
          className="w-full px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}
